import { capitalize, type ComputedRef, type DeepReadonly, type InjectionKey, type Ref } from "vue";
import moment from "moment";
import type { Moment } from "moment";
import {
  MetricPeriodType,
  type IncomeStatementAccountSpecificationsQuery,
  type RawBalanceAccountSpecificationsQuery,
  type IncomeStatementAccountSpecificationsPortfolioQuery,
  type RawBalanceAccountSpecificationsPortfolioQuery,
  type BalancePropertyQuery,
  type BalancePortfolioQuery,
  type ResultsPropertyQuery,
  type ResultsPortfolioQuery,
  AssetManagementCategoryAggregationKind,
  AssetManagementCategoryType,
} from "~/graphql/generated/graphql";
import { type TranslationKey, filterUniqueOn, getCurrentPeriod, getSet, groupBy, sum } from "~/helpers/common-helpers";
import type { DateRangeConfig, NumberDisplay, BudgetsConfig } from "~/helpers/user-preferences-helpers";
import type { Currency } from "~/helpers/apollo/types";
import { hasValue } from "~/helpers/common-helpers";
import type { BudgetRecordsEntity, Category, Property } from "~/helpers/entities/property-helpers";
import { getPeriodBudgetsIntersect, type AccountBudgetCell, type CategoryBudgetCell } from "~/helpers/budgets-helpers";
export type { AccountBudgetCell, CategoryBudgetCell } from "~/helpers/budgets-helpers";
import type { Account } from "~/helpers/entities/account-helpers";
import type { BenchmarkGroupOption, DateRange } from "~/helpers/modules/opex-helpers";
import type { UIColor } from "@/lib/design-system";

export type FinancialStatement = {
  id: string;
  explanation?: Nullable<string>;
  entryDate?: Nullable<string>;
  actualCostWithVat?: Nullable<number>;
  currency: string;
};

// Small utility pure functions for improved clarity and reusability
const isEvenIndex = (index: number): boolean => (index + 1) % 2 === 0;

const formatPeriodKey = (period: Moment): string => period.format("YYYY-MM-DD");

const formatPropertyKey = (property: any): string => property.id;

const getBudgetValue = (columnDataObject: ColumnDataObject | undefined, budgetId: string) =>
  columnDataObject?.budgetValues.find((bv) => bv.budgetId === budgetId);

const calculateChangePercentage = (change: number, total: number): number => (change * 100) / Math.abs(Math.round((total - change) * 100) / 100);

const calculateBudgetDifference = (total: number, budgetValue: number): number => total - budgetValue;

const calculateColumnSpan = (headers: ValueColumnHeaderConfig[]): number => headers.reduce((curr, next) => curr + next.colSpan, 0);

export const fitDate = (_date: Moment, periodType: MetricPeriodType, endOf?: boolean) => {
  const date = moment.utc(_date);
  switch (periodType) {
    case "MONTHLY":
      return endOf ? date.endOf("month") : date.startOf("month");
    case "QUARTERLY":
      return endOf ? date.endOf("quarter") : date.startOf("quarter");
    case "YEARLY":
      return endOf ? date.endOf("year") : date.startOf("year");
    default:
      return date;
  }
};

export const fitDateString = (dateStr: string, periodType: MetricPeriodType, endOf?: boolean) => fitDate(moment.utc(dateStr), periodType, endOf);

const findBudgetValue = (budgetValues: any[], budgetId: string) => budgetValues.find((bv) => bv.budgetId === budgetId);

export const getPeriodHeader = (period: Moment, periodType: MetricPeriodType | null) => {
  switch (periodType) {
    case "MONTHLY":
      return capitalize(period.format("MMM YYYY"));
    case "QUARTERLY":
      return `Q${period.quarter()} ${period.year()}`;
    case "YEARLY":
      return capitalize(period.format("YYYY"));
    default:
      return " - ";
  }
};

export type CategoryCell = Omit<
  NonNullable<BalancePropertyQuery & BalancePortfolioQuery & ResultsPropertyQuery & ResultsPortfolioQuery>["categoryCells"][number],
  "__typename"
>;

export type PropertyCategoryCell = CategoryCell & { propertyId: string };

export interface ColumnHeaderConfigBase {
  type: ColumnHeaderConfigType;
  label?:
    | { value: string }
    | {
        translationKey: TranslationKey;
        values?: Record<string, string | number>;
      };
  colSpan: number;
  even: boolean;
  key: string;
}

export interface ValueHeaderConfigBase extends ColumnHeaderConfigBase {
  groupHeader: GroupColumnHeaderConfig;
  value: ColumnHeaderConfigValue;
  type: ValueColumnHeaderType;
  color?: (row: AccountsRow, value: number) => UIColor;
}

export type ColumnHeaderConfigType = ColumnHeaderConfig["type"];
export type ColumnHeaderConfigValue = ValueColumnHeaderConfig["value"];

export interface PeriodColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "period";
  period: Moment;
}

export interface PropertyColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "property";
  property: Property;
}

export interface GrandTotalColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "grandTotal";
}

export interface TotalColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "total";
  value: (row: AccountsRow) => number;
}

export interface ChangeColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "change";
  value: (row: AccountsRow) => number;
}

export interface ChangePercentageColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "changePercentage";
  value: (row: AccountsRow) => number;
}

export interface BudgetColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budget";
  value: (row: AccountsRow) => number;
}

export interface BudgetDiffColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetDiff";
  value: (row: AccountsRow) => number;
}

export interface BudgetForecastColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetForecast";
  value: (row: AccountsRow) => number;
}

export type ValueColumnHeaderType = ValueColumnHeaderConfig["type"];

export type ValueColumnHeaderConfig =
  | TotalColumnHeaderConfig
  | ChangePercentageColumnHeaderConfig
  | ChangeColumnHeaderConfig
  | BudgetColumnHeaderConfig
  | BudgetDiffColumnHeaderConfig
  | BudgetForecastColumnHeaderConfig;

export type GroupColumnHeaderConfig = PropertyColumnHeaderConfig | PeriodColumnHeaderConfig | GrandTotalColumnHeaderConfig;

export type ColumnHeaderConfig = GroupColumnHeaderConfig | ValueColumnHeaderConfig;

//#endregion

export type ExportRow = { values: number[]; name: string; level: number; sum?: boolean; expanded?: boolean };

export type ExportRows = ExportRow[];

export type AccountSpecificationsQuery = IncomeStatementAccountSpecificationsQuery &
  IncomeStatementAccountSpecificationsPortfolioQuery &
  RawBalanceAccountSpecificationsQuery &
  RawBalanceAccountSpecificationsPortfolioQuery;

export type AccountCell = {
  accountGroup: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  categoryId: string;
  period: string;
  isBlueprint: boolean;
  propertyId: string;

  totalCostWithVat: number;
  change: number;
  changePercentage: number;
};

export type AccountType = AssetManagementCategoryType.IncomeStatement | AssetManagementCategoryType.BalanceSheet;

export const getAccountType = (ancestors: AssetManagementCategoryType[]): AccountType | undefined =>
  ancestors.find(
    (t): t is AccountType => t === AssetManagementCategoryType.IncomeStatement || t === AssetManagementCategoryType.BalanceSheet
  );

export type AccountRow = {
  id: string;
  categoryId: string;
  accountName: string;
  displayName: string;
  columnDataObjects: ColumnDataObject[];
  type: AccountType;
  accountNumber: string;
};

export type AccountsRow = AccountRow | CategoryRow;

export type FinancialStatementsInput = {
  id: string;
  entryDateStart: Moment;
  entryDateEnd: Moment;
};

export type StatementsModalContext = {
  account: Ref<AccountRow | undefined>;
  open: Ref<boolean>;
  header: Ref<GroupColumnHeaderConfig | undefined>;
};

export type BalanceHeaders = {
  groupHeaders: GroupColumnHeaderConfigSet;
  valueHeaders: ValueColumnHeaderConfig[][];
};

export type AccountsActions = {
  loadAccounts(categoryIdOrIds: string | string[]): Promise<void>;
  loadAllAccounts(): Promise<void>;
  progress: ComputedRef<{ current: number; total: number }>;
};

export type FinanceAccountsStatementsConfig = {
  account: AccountRow;
  header: GroupColumnHeaderConfig;
  propertyIds: string[];
} & DateRangeConfig;

export const getAccountAggregationKey = (
  account: {
    id?: string | null;
    accountId?: string | null;
    accountNumber?: string | null;
    categoryId?: string | null;
    propertyId?: string | null;
  }
) => {
  // Prefer stable account IDs so same-number accounts from different sources
  // are kept as distinct rows/cell buckets.
  const stableId = account.accountId ?? account.id;
  if (stableId) return stableId;

  // Fallback for older shapes where IDs are not present.
  if (account.accountNumber && account.categoryId) return `${account.accountNumber}::${account.categoryId}`;
  if (account.accountNumber && account.propertyId) return `${account.accountNumber}::${account.propertyId}`;
  return account.accountNumber ?? undefined;
};

export const TableLayoutInjectionKey = Symbol("tableLayoutContext") as InjectionKey<{ indentFromLevel: number }>;
export const BalanceHeadersInjectionKey = Symbol("balanceTableContext") as InjectionKey<ComputedRef<BalanceHeaders>>;
export const AccountsActionsInjectionKey = Symbol("accountsActionsContext") as InjectionKey<AccountsActions>;
export const BalanceNumberDisplayInjectionKey = Symbol("balanceTableContext") as InjectionKey<ComputedRef<NumberDisplay>>;
export const StatementsModalContextInjectionKey = Symbol("statementsModalContext") as InjectionKey<StatementsModalContext>;
export const BudgetBalanceTotalsInjectionKey = Symbol("budgetBalanceTotals") as InjectionKey<ComputedRef<Map<string, Map<string, number>>>>;

export enum BalanceAndIncomeQueryType {
  Property,
  Portfolio,
}

export type BalanceAndIncomeQueryParameters = {
  propertyId?: string | null;
  portfolioId?: string | null;
};

export const balanceDateRangeYearly = ["currentPreviousYear", "latest3", "latest5", "custom"] as const;
export const balanceDateRangeQuarterly = ["latest4", "latest8", "currentYear", "previousYear", "custom"] as const;
export const balanceDateRangeMonthly = ["currentYear", "previousYear", "currentQuarter", "previousQuarter", "yearToDate", "latest12", "custom"] as const;
export type BalanceDateRange = (typeof balanceDateRangeYearly | typeof balanceDateRangeQuarterly | typeof balanceDateRangeMonthly)[number];

export const setToPreviousPeriod = (date: Moment, periodType: MetricPeriodType) => {
  switch (periodType) {
    case MetricPeriodType.Monthly:
      date.subtract(1, "month");
      date.startOf("month");
      break;
    case MetricPeriodType.Quarterly:
      date.subtract(1, "quarter");
      date.startOf("quarter");
      break;
    case MetricPeriodType.Yearly:
      date.subtract(1, "year");
      date.startOf("year");
      break;
  }
};

export const getBudgetDateRange = (budget: Budget) => {
  return getDateRange(
    "custom",
    MetricPeriodType.Monthly,
    moment.utc(budget.validFrom).startOf("year").format("YYYY-MM-DD"),
    moment.utc(budget.validTo).endOf("year").format("YYYY-MM-DD")
  );
};

export const getDateConfigRange = (config: DateRangeConfig) =>
  getDateRange(config.dateRange, config.periodType, config.customDateStart, config.customDateEnd, config.referenceDate);

export const getDateRange = (
  balanceDateRange: BalanceDateRange,
  periodType: MetricPeriodType,
  customDateStart?: string,
  customDateEnd?: string,
  referenceDate?: string
): DateRange => {
  const newDate = () => moment.utc(referenceDate);
  switch (balanceDateRange) {
    case "currentPreviousYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"), to: fitDate(newDate(), MetricPeriodType.Yearly) };
    case "currentYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly), to: fitDate(newDate(), MetricPeriodType.Yearly).endOf("year") };
    case "yearToDate":
      return {
        from: fitDate(newDate().startOf("year"), MetricPeriodType.Monthly),
        to: fitDate(newDate(), MetricPeriodType.Monthly).subtract(1, "month").endOf("month"),
      };
    case "previousYear":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year").endOf("year"),
      };
    case "latest3":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(3, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "latest5":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(5, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "currentQuarter":
      return { from: fitDate(newDate(), MetricPeriodType.Quarterly), to: fitDate(newDate(), MetricPeriodType.Quarterly).endOf("quarter") };
    case "previousQuarter":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter").endOf("quarter"),
      };
    case "latest4":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(4, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest8":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(8, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest12":
      return {
        from: fitDate(newDate(), MetricPeriodType.Monthly).subtract(12, "month"),
        to: fitDate(newDate(), MetricPeriodType.Monthly).subtract(1, "month"),
      };
    case "custom":
      const from = moment.utc(customDateStart, "YYYY-MM-DD").startOf("month");
      const to = moment.utc(customDateEnd, "YYYY-MM-DD").endOf("month");

      switch (periodType) {
        case MetricPeriodType.Monthly:
          from.startOf("month");
          to.endOf("month");
          break;
        case MetricPeriodType.Quarterly:
          from.startOf("quarter");
          to.endOf("quarter");
          break;
        case MetricPeriodType.Yearly:
          from.startOf("year");
          to.endOf("year");
          break;
      }

      return { from, to };
    default:
      // Default fallback for unknown date range types (e.g., from newer branches)
      console.warn(`Unknown balanceDateRange: ${balanceDateRange}. Falling back to current year.`);
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly),
        to: fitDate(newDate(), MetricPeriodType.Yearly).endOf("year"),
      };
  }
};

export const getDateRangeEntries = ({ from, to }: { from: Moment; to: Moment }, periodType: MetricPeriodType) => {
  const entries: Moment[] = [];

  const current = fitDate(moment.utc(to), periodType);

  while (current.isSameOrAfter(from)) {
    entries.unshift(moment.utc(current));

    setToPreviousPeriod(current, periodType);
  }

  return entries;
};

export const getEmptyColumnDataObject = (): ColumnDataObject => ({
  budgetValues: [],
  totalCostWithVat: 0,
  change: 0,
});

export interface GroupColumnHeaderConfigSetBase {
  type: GroupColumnHeaderConfigSetType;
  headers: Object[];
}

/**
 * Configuration for period-based column grouping.
 * Used when displaying balance data grouped by time periods (monthly, quarterly, yearly).
 */
export interface PeriodColumnHeaderConfigSet extends GroupColumnHeaderConfigSetBase {
  type: "period";
  headers: (PeriodColumnHeaderConfig | GrandTotalColumnHeaderConfig)[];
}

/**
 * Configuration for property-based column grouping.
 * Used when displaying balance data grouped by properties in a portfolio.
 * Enables side-by-side comparison of properties within the same time period.
 */
export interface PropertyColumnHeaderConfigSet extends GroupColumnHeaderConfigSetBase {
  type: "property";
  headers: (PropertyColumnHeaderConfig | GrandTotalColumnHeaderConfig)[];
}

/**
 * Union type for all supported column grouping configurations.
 *
 * USAGE:
 * - Period grouping: Traditional view showing time progression for accounts
 * - Property grouping: Portfolio view showing property comparison for accounts
 * - Grand total: Aggregated view showing totals across all periods/properties
 *
 * The type determines how data is aggregated and displayed in the balance table.
 */
export type GroupColumnHeaderConfigSet = PropertyColumnHeaderConfigSet | PeriodColumnHeaderConfigSet;

export type GroupColumnHeaderConfigSetType = GroupColumnHeaderConfigSet["type"];

export type AccountHeadersGroupConfig =
  | { type: "period"; headers: (PeriodColumnHeaderConfig | GrandTotalColumnHeaderConfig)[] }
  | { type: "property"; headers: (PropertyColumnHeaderConfig | GrandTotalColumnHeaderConfig)[] };

const createAccountGrandTotalGrouping = (data: AccountCell[], budgetCells: AccountBudgetCell[]) => {
  const groupedDataCells = groupBy(data, {
    accountKey: { type: "string", getKey: getAccountAggregationKey },
  });

  const groupedBudgetCells = groupBy(budgetCells, {
    accountKey: { type: "string", getKey: (item) => item.accountKey },
  });

  return { groupedDataCells, groupedBudgetCells };
};

type GroupingResult = {
  groupedDataCells: any;
  groupedBudgetCells: any;
};

const createHeaderMapper = <THeader extends { type: string }>(
  headers: THeader[],
  primaryGrouping: GroupingResult,
  grandTotalGrouping: GroupingResult,
  getPrimaryKey: (header: THeader) => Record<string, any> | undefined,
  getGrandTotalKey: () => Record<string, any> | undefined
) => {
  return headers.map((header) => {
    if (header.type === "grandTotal") {
      const key = getGrandTotalKey();
      if (!key) return;

      return {
        budgetCells: grandTotalGrouping.groupedBudgetCells.get(key)?.items ?? [],
        dataCells: grandTotalGrouping.groupedDataCells.get(key)?.items ?? [],
        dataObject: getEmptyColumnDataObject(),
      };
    }

    const key = getPrimaryKey(header);
    if (!key) return;

    return {
      budgetCells: primaryGrouping.groupedBudgetCells.get(key)?.items ?? [],
      dataCells: primaryGrouping.groupedDataCells.get(key)?.items ?? [],
      dataObject: getEmptyColumnDataObject(),
    };
  });
};

/**
 * Factory function to create header group data accessors for account rows.
 *
 * This function returns a curried function that can extract the appropriate data
 * for each header group from account rows, handling both period and property grouping.
 *
 * ARCHITECTURE:
 * The function uses the visitor pattern to handle different grouping types,
 * returning a function that maps each header to its corresponding data cells.
 *
 * @param groupColumnHeaders - Configuration defining how columns should be grouped
 * @param data - Raw account cell data to be grouped
 * @param periodType - Time period granularity for period-based grouping
 * @param budgetCells - Budget data to be associated with accounts
 * @returns Function that maps account rows to their grouped data objects
 */
export const getAccountHeaderGroups = (
  groupColumnHeaders: AccountHeadersGroupConfig,
  {
    data,
    periodType,
    budgetCells,
  }: {
    periodType: MetricPeriodType;
    budgetCells: AccountBudgetCell[];
    data: AccountCell[];
  }
): ((accountRow: AccountRow) => ({ dataCells: AccountCell[]; budgetCells: AccountBudgetCell[]; dataObject: ColumnDataObject } | undefined)[]) => {
  switch (groupColumnHeaders.type) {
    case "period": {
      const groupedDataCells = groupBy(data, {
        period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
        accountKey: { type: "string", getKey: getAccountAggregationKey },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        period: { type: "period", getKey: (item) => item.period },
        accountKey: { type: "string", getKey: (item) => item.accountKey },
      });

      const grandTotal = createAccountGrandTotalGrouping(data, budgetCells);

      return (row) => {
        const accountKey = getAccountAggregationKey(row);
        return createHeaderMapper(
          groupColumnHeaders.headers,
          { groupedDataCells, groupedBudgetCells },
          grandTotal,
          (header) => (accountKey && header.type === "period" ? { period: header.period, accountKey } : undefined),
          () => (accountKey ? { accountKey } : undefined)
        );
      };
    }
    case "property": {
      const groupedDataCells = groupBy(data, {
        propertyId: { type: "string", getKey: (item) => item.propertyId },
        accountKey: { type: "string", getKey: getAccountAggregationKey },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        propertyId: { type: "string", getKey: (item) => item.propertyId },
        accountKey: { type: "string", getKey: (item) => item.accountKey },
      });

      const grandTotal = createAccountGrandTotalGrouping(data, budgetCells);

      return (row) => {
        const accountKey = getAccountAggregationKey(row);
        return createHeaderMapper(
          groupColumnHeaders.headers,
          { groupedDataCells, groupedBudgetCells },
          grandTotal,
          (header) => (accountKey && header.type === "property" ? { propertyId: header.property.id, accountKey } : undefined),
          () => (accountKey ? { accountKey } : undefined)
        );
      };
    }
  }
};

const createCategoryGrandTotalGrouping = (data: PropertyCategoryCell[], budgetCells: CategoryBudgetCell[]) => {
  const groupedDataCells = groupBy(data, {
    categoryId: { type: "string", getKey: (item) => item.categoryId },
  });

  const groupedBudgetCells = groupBy(budgetCells, {
    categoryId: { type: "string", getKey: (item) => item.categoryId },
  });

  return { groupedDataCells, groupedBudgetCells };
};

/**
 * Factory function to create header group data accessors for category rows.
 *
 * Similar to getAccountHeaderGroups but operates on category-level data aggregation.
 * Categories are higher-level groupings that contain multiple accounts.
 *
 * @param groupColumnHeaders - Configuration defining how columns should be grouped
 * @param data - Raw category cell data with property association
 * @param periodType - Time period granularity for period-based grouping
 * @param budgetCells - Budget data to be associated with categories
 * @returns Function that maps category rows to their grouped data objects
 */
export const getCategoryHeaderGroups = (
  groupColumnHeaders: GroupColumnHeaderConfigSet,
  {
    data,
    periodType,
    budgetCells,
  }: {
    periodType: MetricPeriodType;
    budgetCells: CategoryBudgetCell[];
    data: PropertyCategoryCell[];
  }
): ((row: CategoryRow) => ({ dataCells: CategoryCell[]; budgetCells: CategoryBudgetCell[]; dataObject: ColumnDataObject } | undefined)[]) => {
  switch (groupColumnHeaders.type) {
    case "period": {
      const groupedDataCells = groupBy(data, {
        period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
        categoryId: { type: "string", getKey: (item) => item.categoryId },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        period: { type: "period", getKey: (item) => item.period },
        categoryId: { type: "string", getKey: (item) => item.categoryId },
      });

      const grandTotal = createCategoryGrandTotalGrouping(data, budgetCells);

      return (row) =>
        createHeaderMapper(
          groupColumnHeaders.headers,
          { groupedDataCells, groupedBudgetCells },
          grandTotal,
          (header) => (header.type === "period" ? { period: header.period, categoryId: row.id } : undefined),
          () => ({ categoryId: row.id })
        );
    }
    case "property": {
      const groupedDataCells = groupBy(data, {
        categoryId: { type: "string", getKey: (item) => item.categoryId },
        propertyId: { type: "string", getKey: (item) => item.propertyId },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        categoryId: { type: "string", getKey: (item) => item.categoryId },
        propertyId: { type: "string", getKey: (item) => item.propertyId },
      });

      const grandTotal = createCategoryGrandTotalGrouping(data, budgetCells);

      return (row) =>
        createHeaderMapper(
          groupColumnHeaders.headers,
          { groupedDataCells, groupedBudgetCells },
          grandTotal,
          (header) => (header.type === "property" ? { propertyId: header.property.id, categoryId: row.id } : undefined),
          () => ({ categoryId: row.id })
        );
    }
  }
};

export const isZeroRow: {
  (values: number[]): boolean;
  (values: ColumnDataObject[]): boolean;
} = (valuesOrObjects: number[] | ColumnDataObject[]) => {
  const isZeroValue = (v: number) => Math.round(v * 1000) / 1000 === 0;
  const isZeroObject = (o: ColumnDataObject) => o.budgetValues.every((bv) => isZeroValue(bv.value)) && isZeroValue(o.totalCostWithVat);

  return valuesOrObjects.every((v) => (typeof v === "number" ? isZeroValue(v) : isZeroObject(v)));
};

export type Budget = BudgetRecordsEntity & {
  id: string;
  createdAt?: Nullable<string>;
  description?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  name: string;
  propertyId?: Nullable<string>;
};

export type ColumnDataBudgetValue = { budgetId: string; value: number; forecast: number };

export type ColumnDataObject = {
  change: number;
  totalCostWithVat: number;
  budgetValues: ColumnDataBudgetValue[];
};

export type CategoryRow = {
  id: string;
  name: string;
  columnDataObjects: ColumnDataObject[];
  level: number;
  children: CategoryRow[];
  parent: CategoryRow | undefined;
  hashes: string[];
  ancestorCategoryTypes: AssetManagementCategoryType[];
  aggregationKind: AssetManagementCategoryAggregationKind | undefined;
  state: {
    expanded: boolean;
    disabled: boolean;
    accounts: {
      loading: boolean;
      items: AccountRow[] | undefined;
    };
  };
};

export const getSetValue = <K, V>(key: K, map: Map<K, V>, create: () => V) => {
  let value = map.get(key);

  if (!value) {
    value = create();

    map.set(key, value);
  }

  return value;
};

export const createCategoryRow = (
  category: Category,
  parent: CategoryRow | undefined,
  children: CategoryRow[],
  level: number,
  ancestorCategoryTypes: AssetManagementCategoryType[]
): CategoryRow => ({
  id: category.id,
  name: category.name ?? "",
  level,
  aggregationKind: category.aggregationKind ?? undefined,
  hashes: [],
  columnDataObjects: [],
  state: {
    accounts: {
      loading: false,
      items: [],
    },
    disabled: false,
    expanded: level < 2 && !!children.length,
  },
  children,
  parent,
  ancestorCategoryTypes,
});

/**
 * BANDAID (remove once backend treats balance categories natively):
 * When a BALANCE_SHEET-rooted category is served through the income-statement endpoint
 * (aggregationKind === INCOME), the server applies its income-sign convention to the
 * values. Flip `totalCostWithVat` and `change` here so balance values render with the
 * correct sign. `changePercentage` is sign-invariant under this flip and left alone.
 */
export const bandaidSignFlipCell = <T extends { totalCostWithVat: number; change: number }>(
  cell: T,
  accountType: AccountType | undefined,
  aggregationKind: AssetManagementCategoryAggregationKind | undefined
): T =>
  accountType === AssetManagementCategoryType.BalanceSheet && aggregationKind === AssetManagementCategoryAggregationKind.Income
    ? { ...cell, totalCostWithVat: -cell.totalCostWithVat, change: -cell.change }
    : cell;

/**
 * Resolve each category's effective `aggregationKind` by inheriting from the nearest
 * ancestor that has one set. `aggregationKind` is a server-controlled field that
 * decides which finance endpoint (raw-balance vs income-statement) returns the
 * category's cells — it's orthogonal to `categoryType`/`AccountType`.
 */
export const computeCategoryIdToAggregationKind = (categories: Category[]): Map<string, AssetManagementCategoryAggregationKind> => {
  const byId = new Map(categories.map((c) => [c.id, c] as const));
  const cache = new Map<string, AssetManagementCategoryAggregationKind>();

  const resolve = (c: Category): AssetManagementCategoryAggregationKind | undefined => {
    const cached = cache.get(c.id);
    if (cached) return cached;
    if (c.aggregationKind) {
      cache.set(c.id, c.aggregationKind);
      return c.aggregationKind;
    }
    const parent = c.parentCategoryId ? byId.get(c.parentCategoryId) : undefined;
    const inherited = parent ? resolve(parent) : undefined;
    if (inherited) cache.set(c.id, inherited);
    return inherited;
  };

  categories.forEach(resolve);
  return cache;
};

export const computeCategoryIdToAccountType = (categories: Category[]): Map<string, AccountType> => {
  const ancestorMap = computeAncestorCategoryTypes(categories);
  const out = new Map<string, AccountType>();
  ancestorMap.forEach((ancestors, categoryId) => {
    const accountType = getAccountType(ancestors);
    if (accountType) out.set(categoryId, accountType);
  });
  return out;
};

export const computeAncestorCategoryTypes = (categories: Category[]): Map<string, AssetManagementCategoryType[]> => {
  const byId = new Map(categories.map((c) => [c.id, c] as const));
  const cache = new Map<string, AssetManagementCategoryType[]>();

  const collect = (c: Category): AssetManagementCategoryType[] => {
    const cached = cache.get(c.id);
    if (cached) return cached;
    const parent = c.parentCategoryId ? byId.get(c.parentCategoryId) : undefined;
    const parentAncestors = parent ? collect(parent) : [];
    const own = c.categoryType ? [c.categoryType] : [];
    const chain = [...parentAncestors, ...own];
    cache.set(c.id, chain);
    return chain;
  };

  categories.forEach(collect);
  return cache;
};

export const buildCategoryTree = <T extends { children: T[] }>(
  rootIds: string[],
  allCategories: Category[],
  create: (category: Category, parent: T | undefined, children: T[], level: number) => T
): T[] => {
  const childrenGroups = groupBy(allCategories, { parentCategoryId: { type: "string", getKey: (item) => item.parentCategoryId } });
  const parents: Map<string, T> = new Map();

  const toBranch = (c: Category, level: number): T => {
    const parent = c.parentCategoryId ? parents.get(c.parentCategoryId) : undefined;
    const children = (childrenGroups.get({ parentCategoryId: c.id })?.items ?? []).map((c) => toBranch(c, level + 1));
    const row = create(c, parent, children, level);

    parents.set(c.id, row);

    return row;
  };

  const roots = rootIds.map((rootId) => allCategories.find((cat) => cat.id === rootId)).filter(hasValue);

  return roots.map((root) => toBranch(root, 0));
};

const emptyBudgetValue: (budgetId: string) => ColumnDataBudgetValue = (budgetId: string) => ({ budgetId, value: 0, forecast: 0 });

/** Runs bottom up */
export const runTree = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => void, level = 0) => {
  root.children.forEach((c) => runTree(c, handler, level + 1));

  handler(root);
};

/** Runs top down. Can be stopped early by returning false */
export const runTreeDown = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => boolean, level = 0): boolean => {
  let shouldContinue = handler(root);

  if (shouldContinue) {
    for (let child of root.children) {
      shouldContinue = runTreeDown(child, handler, level + 1);

      if (!shouldContinue) return false;
    }
  }

  return shouldContinue;
};

// Pure functions for data aggregation
const aggregateChildrenTotals = (children: CategoryRow[], index: number) => {
  // For balance data with property grouping, children already contain the correct values
  // since each child was processed with the same aggregation logic
  return {
    totalCostWithVat: sum(children.map((child) => child.columnDataObjects[index]?.totalCostWithVat ?? 0)),
    change: sum(children.map((child) => child.columnDataObjects[index]?.change ?? 0)),
  };
};

const getLatestCell = (dataCells: (CategoryCell | AccountCell)[]) => {
  if (dataCells.length === 0) return undefined;

  // Assumes period is a string in ISO format (e.g., "2023-01-01")
  const sortedCells = dataCells.sort((a, b) => a.period.localeCompare(b.period));

  return sortedCells[sortedCells.length - 1];
};

export const aggregateDataCells = (dataCells: (CategoryCell | AccountCell)[], accountType: AccountType) => {
  let cellsToAggregate: (CategoryCell | AccountCell)[];

  switch (accountType) {
    case AssetManagementCategoryType.BalanceSheet: {
      const latestDate = getLatestCell(dataCells.map((cell) => (cell && typeof cell === "object" && !cell.isBlueprint ? cell : null)).filter(hasValue))?.period;
      cellsToAggregate = dataCells.filter((cell) => cell.period === latestDate && !cell.isBlueprint);
      break;
    }
    case AssetManagementCategoryType.IncomeStatement: {
      cellsToAggregate = dataCells;
      break;
    }
  }

  return {
    totalCostWithVat: sum(cellsToAggregate.map((cell) => cell.totalCostWithVat)),
    change: sum(cellsToAggregate.map((cell) => cell.change)),
    hashes: cellsToAggregate
      .filter((cell) => "hash" in cell)
      .map((cell) => cell.hash)
      .filter(Boolean),
  };
};

const aggregateChildrenBudgetValues = (children: CategoryRow[], index: number): ColumnDataBudgetValue[] => {
  const budgetValues: ColumnDataBudgetValue[] = [];
  const childrenBudgetValues = children.flatMap((c) => c.columnDataObjects[index]?.budgetValues ?? []);

  for (let childBudgetValue of childrenBudgetValues) {
    const { budgetId, value, forecast } = childBudgetValue;

    const budgetValue = getSet(
      budgetValues,
      (bv) => bv.budgetId === budgetId,
      () => emptyBudgetValue(budgetId)
    );

    budgetValue!.value += value;
    budgetValue!.forecast += forecast;
  }

  return budgetValues;
};

const applyBudgetCells = (budgetValues: ColumnDataBudgetValue[], budgetCells: CategoryBudgetCell[]): void => {
  for (let budgetCell of budgetCells) {
    const { budgetId, forecast, value } = budgetCell;

    const budgetValue = getSet(
      budgetValues,
      (bv) => bv.budgetId === budgetId,
      () => emptyBudgetValue(budgetId)
    );

    budgetValue!.value += value;
    budgetValue!.forecast += forecast;
  }
};

/**
 * Aggregates financial data for a single category column.
 * This pure function combines children totals, direct data cells, and budget information
 * into a single ColumnDataObject.
 */
const aggregateCategoryColumn = (
  categoryRow: CategoryRow,
  index: number,
  dataCells: CategoryCell[],
  budgetCells: CategoryBudgetCell[],
  accountType: AccountType,
  headerConfigType: string
): { columnDataObject: ColumnDataObject; hashes: string[] } => {
  const columnDataObject = getEmptyColumnDataObject();

  // Aggregate children data
  const childrenTotals = aggregateChildrenTotals(categoryRow.children, index);

  // Aggregate direct data cells
  const cellAggregates = aggregateDataCells(dataCells, accountType);

  // Combine totals
  columnDataObject.totalCostWithVat = childrenTotals.totalCostWithVat + cellAggregates.totalCostWithVat;
  columnDataObject.change = childrenTotals.change + cellAggregates.change;

  // Handle budget values
  columnDataObject.budgetValues = aggregateChildrenBudgetValues(categoryRow.children, index);
  applyBudgetCells(columnDataObject.budgetValues, budgetCells);

  return {
    columnDataObject,
    hashes: cellAggregates.hashes,
  };
};

/**
 * Aggregates financial data for a single account column.
 * This pure function combines direct data cells and budget information
 * into a single ColumnDataObject for accounts (which don't have children).
 */
const aggregateAccountColumn = (
  dataCells: AccountCell[],
  budgetCells: AccountBudgetCell[],
  accountType: AccountType,
  headerConfigType: string
): ColumnDataObject => {
  const columnDataObject = getEmptyColumnDataObject();

  // Aggregate direct data cells
  const cellAggregates = aggregateDataCells(dataCells, accountType);

  // Combine totals
  columnDataObject.totalCostWithVat = cellAggregates.totalCostWithVat;
  columnDataObject.change = cellAggregates.change;

  // Apply budget cells — accumulate per budgetId so columns backed by multiple cells
  // (e.g. the grand-total column, which groups every period's cells together) sum correctly
  // instead of keeping only the last cell. Mirrors applyBudgetCells in the category path.
  for (let budgetCell of budgetCells) {
    const { budgetId, forecast, value } = budgetCell;

    const budgetValue = getSet(
      columnDataObject.budgetValues,
      (bv) => bv.budgetId === budgetId,
      () => emptyBudgetValue(budgetId)
    );

    budgetValue!.value += value;
    budgetValue!.forecast += forecast;
  }

  return columnDataObject;
};

/**
 * Recompute each row's per-period `change` from this level's own consecutive period totals
 * instead of trusting the backend `change` field.
 *
 * The backend computes income `change` against the previous period the *entity* has data for.
 * When an entity is missing an interior period that the aggregate level does have (e.g. an
 * account with no Q3 activity — a blueprint cell — summed under a category that does have Q3),
 * the backend change is computed across that gap (Q4 vs Q2) and no longer sums correctly up the
 * tree. Deriving change as total(N) − total(N−1) at every level is gap-safe and reproduces the
 * backend value exactly when the data is dense (the period-over-period invariant holds there).
 *
 * The first displayed period's change is preserved via baseline reconstruction
 * (total − backendChange), so any pre-range history the backend already accounted for is kept.
 *
 * Only period columns participate; grand-total / property columns are left untouched.
 */
export const recomputeChangeFromPeriodTotals = (columnDataObjects: ColumnDataObject[], headers: ReadonlyArray<{ type: string }>): void => {
  const periodCols = headers
    .map((h, i) => ({ i, period: (h as PeriodColumnHeaderConfig).period }))
    .filter((x) => x.period != null)
    .sort((a, b) => a.period.valueOf() - b.period.valueOf());

  const firstCol = periodCols.length ? columnDataObjects[periodCols[0].i] : undefined;
  if (!firstCol) return;

  // Implied value just before the first displayed period (preserves pre-range history).
  let runningPrev = (firstCol.totalCostWithVat ?? 0) - (firstCol.change ?? 0);
  for (const { i } of periodCols) {
    const cdo = columnDataObjects[i];
    if (!cdo) continue;
    const total = cdo.totalCostWithVat ?? 0;
    cdo.change = total - runningPrev;
    runningPrev = total;
  }
};

export const sumCategoryRowTree = ({
  budgetCells,
  categoryTree,
  categoryCells,
  headerConfig,
  periodType,
  accountType,
}: {
  headerConfig: GroupColumnHeaderConfigSet;
  periodType: MetricPeriodType;
  categoryTree: CategoryRow;
  categoryCells: PropertyCategoryCell[];
  budgetCells: CategoryBudgetCell[];
  accountType: AccountType;
}) => {
  const headerDataGroups = getCategoryHeaderGroups(headerConfig, { data: categoryCells, periodType, budgetCells });

  runTree(categoryTree, (categoryRow) => {
    const headerData = headerDataGroups(categoryRow);

    categoryRow.columnDataObjects = [];

    for (const [index, group] of headerData.entries()) {
      if (!group) continue;

      const { dataCells, budgetCells, dataObject: columnDataObject } = group;

      // Use pure function for aggregation
      const { columnDataObject: aggregatedData, hashes } = aggregateCategoryColumn(categoryRow, index, dataCells, budgetCells, accountType, headerConfig.type);

      categoryRow.columnDataObjects.push(aggregatedData);
      categoryRow.hashes.push(...hashes);
    }

    // Income `change` is derived from this level's own consecutive period totals (gap-safe),
    // rather than the backend field which skips periods an entity has no data for.
    if (accountType === AssetManagementCategoryType.IncomeStatement) {
      recomputeChangeFromPeriodTotals(categoryRow.columnDataObjects, headerConfig.headers);
    }
  });
};

export const sumAccounts = ({
  accountRows,
  data,
  headerConfig,
  periodType,
  budgetCells,
  accountType,
}: {
  accountRows: AccountRow[];
  data: AccountCell[];
  budgetCells: AccountBudgetCell[];
  headerConfig: AccountHeadersGroupConfig;
  periodType: MetricPeriodType;
  accountType: AccountType;
}) => {
  const headerDataGroups = getAccountHeaderGroups(headerConfig, { data, periodType, budgetCells });

  for (let row of accountRows) {
    row.columnDataObjects = [];

    const headerData = headerDataGroups(row);

    for (const group of headerData) {
      if (!group) {
        console.warn("No group data found for row", row);
        continue;
      }

      const { dataCells: cells, budgetCells } = group;

      // Use pure function for aggregation
      const columnDataObject = aggregateAccountColumn(cells, budgetCells, accountType, headerConfig.type);
      row.columnDataObjects.push(columnDataObject);
    }

    // Income `change` is derived from this level's own consecutive period totals (gap-safe),
    // rather than the backend field which skips periods an account has no data for.
    if (accountType === AssetManagementCategoryType.IncomeStatement) {
      recomputeChangeFromPeriodTotals(row.columnDataObjects, headerConfig.headers);
    }
  }
};

const getAccountDisplayName = (account: { accountNumber?: string | null; accountName?: string | null }) =>
  `${account.accountNumber ?? ""} ${account.accountName ?? ""}`;

export const accountsToRows = (accounts: Account[], type: AccountType) => {
  const accountRows: AccountRow[] = filterUniqueOn(accounts, getAccountAggregationKey).map((cell) => ({
    id: cell.id,
    displayName: getAccountDisplayName(cell),
    accountName: cell.accountName ?? "",
    accountNumber: cell.accountNumber ?? "",
    categoryId: cell.categoryId ?? "",
    columnDataObjects: [],
    type,
  }));

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountCellsToRows = (accountCells: AccountCell[], type: AccountType) => {
  const toRow = (cell: AccountCell): AccountRow => ({
    id: cell.accountId,
    displayName: getAccountDisplayName(cell),
    accountName: cell.accountName,
    accountNumber: cell.accountNumber,
    categoryId: cell.categoryId,
    columnDataObjects: [],
    type,
  });

  const accountRows: AccountRow[] = filterUniqueOn(accountCells, getAccountAggregationKey).map(toRow);

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountRowIsNonZero = (accountRow: AccountRow, budgets: Budget[], benchmark: BenchmarkGroupOption) =>
  accountRow.columnDataObjects.some((item) => {
    const isNonZero = (v: number) => Math.abs(v) > 0.01;

    const budgetsZero = () => {
      if (benchmark !== "budget.diff") return true;

      for (let budget of budgets) {
        const budgetValue = findBudgetValue(item.budgetValues, budget.id);

        if (budgetValue && isNonZero(budgetValue.value)) return false;
      }

      return true;
    };

    return isNonZero(item.totalCostWithVat) || !budgetsZero();
  });

export const getPeriodMetricRange = (period: Moment, periodType: MetricPeriodType) => {
  let to = moment(period);
  const from = moment(period);

  switch (periodType) {
    case "MONTHLY":
      to = to.endOf("month");
      break;
    case "QUARTERLY":
      to = to.endOf("quarter");
      break;
    case "YEARLY":
      to = to.endOf("year");
      break;
  }

  return { from, to };
};

export const getPropertySelectedBudgetIds = (preferences: { propertyBudgets: BudgetsConfig }, propertyId: string) =>
  preferences.propertyBudgets[propertyId] as string[] | undefined;

export const getPropertySelectedBudgets = (preferences: { propertyBudgets: BudgetsConfig }, propertyId: string, budgets: Budget[]) =>
  getPropertySelectedBudgetIds(preferences, propertyId)
    ?.map((id) => budgets.find((budget) => budget.id === id))
    .filter((b) => b !== undefined) ?? [];

export const findCurrencyFromCells = (...cellArrays: CategoryCell[][]): Currency | undefined => {
  for (const cells of cellArrays) {
    if (cells) {
      const cellWithCurrency = cells.find((c) => !!c.currency);

      if (cellWithCurrency) return cellWithCurrency.currency as Currency;
    }
  }
  return undefined;
};

export type FinancialsHeadersOptions = {
  groupColumnType: GroupColumnHeaderConfigSetType;
  selectedBudgets: Budget[];
  valueHeaderKeys: ValueHeaderKey[];
} & (
  | {
      groupColumnType: "period";
      periodType: MetricPeriodType;
      periodRange: DateRange;
    }
  | {
      groupColumnType: "property";
      properties: Property[];
    }
);

export type BudgetValueHeaderKey = "budget" | "budgetForecast" | "budgetDiff";
export type StandardValueHeaderKey = "total" | "change" | "changePercentage";
export type ValueHeaderKey = BudgetValueHeaderKey | StandardValueHeaderKey;

const getChangePercentageCopyColor = (value: number): UIColor => {
  // Zero (rounded) change is de-emphasised; positive green, negative red — using the app's
  // actual palette tokens (the legacy green-900/pink-900/black-300 don't exist in this theme).
  if (Math.round(value) === 0) return "--color-muted-foreground";
  return value > 0 ? "--color-green-300" : "--color-red-400";
};

const getFinancialsValueHeaderKeys = ({
  preferences: { columns = [] },
  orderedAvailableColumns,
}: {
  preferences: { columns?: ValueHeaderKey[] };
  orderedAvailableColumns: ValueHeaderKey[];
}): ValueHeaderKey[] => {
  const selectedSet = new Set(columns);
  const availableSet = new Set(orderedAvailableColumns);

  return Array.from(selectedSet.intersection(availableSet));
};

const getFinancialsValueHeaders = ({
  index,
  groupHeader,
  valueHeaderKeys,
  keyPrefix,
}: {
  index: number;
  groupHeader: GroupColumnHeaderConfig;
  valueHeaderKeys: ValueHeaderKey[];
  keyPrefix: string;
}) => {
  const headers: ValueColumnHeaderConfig[] = [];

  const even = isEvenIndex(index);

  for (const key of valueHeaderKeys) {
    let header: ValueColumnHeaderConfig | undefined = undefined;

    switch (key) {
      case "total": {
        header = {
          type: "total",
          key: keyPrefix + "total",
          label: { translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_VALUE" },
          colSpan: 1,
          value: (row) => row.columnDataObjects[index]?.totalCostWithVat ?? 0,
          even,
          groupHeader,
        };
        break;
      }
      case "change": {
        const hasChangePercentage = valueHeaderKeys.includes("changePercentage");
        header = {
          type: "change",
          key: keyPrefix + "change",
          label: { translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE" },
          colSpan: hasChangePercentage ? 2 : 1,
          value: (row) => row.columnDataObjects[index]?.change ?? 0,
          even,
          groupHeader,
        };
        break;
      }
      case "changePercentage": {
        const hasChange = valueHeaderKeys.includes("change");

        header = {
          type: "changePercentage",
          key: keyPrefix + "changePercentage",
          label: hasChange ? { translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE" } : undefined,
          colSpan: hasChange ? 0 : 1,
          value: (row) => {
            const change = row.columnDataObjects[index]?.change ?? 0;
            const total = row.columnDataObjects[index]?.totalCostWithVat ?? 0;
            return calculateChangePercentage(change, total);
          },
          even,
          groupHeader,
        };
        break;
      }
    }

    if (header) {
      headers.push(header);

      groupHeader.colSpan += header?.colSpan ?? 0;
    }
  }

  return headers;
};

const getFinancialsBudgetValueHeaders = ({
  index,
  groupHeader,
  prefix,
  budgets,
  forecast,
  valueHeaderKeys,
  mergeByName = false,
}: {
  index: number;
  groupHeader: GroupColumnHeaderConfig;
  prefix: string;
  budgets: Budget[];
  forecast: boolean;
  valueHeaderKeys: ValueHeaderKey[];
  /** When true, budgets sharing a name collapse into one column whose value sums them all (used by the grand total). */
  mergeByName?: boolean;
}) => {
  const headers: ValueColumnHeaderConfig[] = [];

  const even = isEvenIndex(index);

  // Each group renders as one budget column. Normally that's one budget per group; with mergeByName,
  // budgets sharing a name are summed into a single column (the first is used for label and sign).
  const budgetGroups: Budget[][] = mergeByName
    ? [
        ...budgets
          .reduce((map, b) => {
            const group = map.get(b.name);
            if (group) group.push(b);
            else map.set(b.name, [b]);
            return map;
          }, new Map<string, Budget[]>())
          .values(),
      ]
    : budgets.map((b) => [b]);

  for (const group of budgetGroups) {
    const budget = group[0];
    if (!budget) continue;
    const ids = group.map((b) => b.id);
    const sumBudgetField = (columnDataObject: ColumnDataObject | undefined, field: "value" | "forecast") =>
      ids.reduce((acc, id) => acc + (getBudgetValue(columnDataObject, id)?.[field] ?? 0), 0);

    for (const key of valueHeaderKeys) {
      let header: ValueColumnHeaderConfig | undefined = undefined;

      switch (key) {
        case "budgetForecast": {
          if (!forecast) break;

          header = {
            type: "budgetForecast",
            colSpan: 1,
            key: prefix + "budgetForecast" + budget.id,
            label: { translationKey: "PORTFOLIO_BALANCE_COLUMNS_BUDGET_FORECAST", values: { budgetDescription: budget?.description ?? "Budget" } },
            value: (row) => {
              const columnDataObject = row.columnDataObjects[index];
              return sumBudgetField(columnDataObject, "forecast");
            },
            even,
            groupHeader,
          };
          break;
        }
        case "budget": {
          header = {
            type: "budget",
            colSpan: 1,
            key: prefix + "budget" + budget.id,
            label: { translationKey: "PORTFOLIO_BALANCE_COLUMNS_BUDGET_TOTAL", values: { budgetDescription: budget?.description ?? "Budget" } },
            value: (row) => {
              const columnDataObject = row.columnDataObjects[index];
              return sumBudgetField(columnDataObject, "value");
            },
            even,
            groupHeader,
          };
          break;
        }
        case "budgetDiff": {
          if (forecast) break;

          header = {
            type: "budgetDiff",
            colSpan: 1,
            key: prefix + "budgetDiff" + budget.id,
            label: { translationKey: "PORTFOLIO_BALANCE_COLUMNS_BUDGET_DIFF", values: { budgetDescription: budget?.description ?? "Budget" } },
            value: (row) => {
              const columnDataObject = row.columnDataObjects[index];
              const total = columnDataObject?.totalCostWithVat ?? 0;
              const budgetAmount = sumBudgetField(columnDataObject, "value");
              return calculateBudgetDifference(total, budgetAmount);
            },
            even,
            groupHeader,
          };
        }
      }

      if (header) {
        headers.push(header);

        groupHeader.colSpan += header?.colSpan ?? 0;
      }
    }
  }

  return headers;
};

const generateGrandTotalHeaders = ({
  index,
  valueHeaderKeys,
  selectedBudgets,
}: {
  index: number;
  valueHeaderKeys: ValueHeaderKey[];
  selectedBudgets: Budget[];
}): { groupHeader: GrandTotalColumnHeaderConfig; valueHeaders: ValueColumnHeaderConfig[] } => {
  const groupHeader: GrandTotalColumnHeaderConfig = {
    type: "grandTotal",
    key: "grandTotal",
    label: { value: "Grand Total" },
    colSpan: 0,
    even: isEvenIndex(index),
  };

  const headers = getFinancialsValueHeaders({
    index,
    groupHeader,
    valueHeaderKeys,
    keyPrefix: "grandTotal",
  });

  headers.push(
    ...getFinancialsBudgetValueHeaders({ index, forecast: false, groupHeader, prefix: "grandTotal", budgets: selectedBudgets, valueHeaderKeys, mergeByName: true })
  );

  groupHeader.colSpan = calculateColumnSpan(headers);

  return { groupHeader, valueHeaders: headers };
};

export const getFinancialsPeriodHeaders = ({
  preferences,
  selectedBudgets,
}: {
  preferences: { columns?: ValueHeaderKey[]; useChangeColors?: boolean; showGrandTotal?: boolean } & DateRangeConfig;
  selectedBudgets: Budget[];
}): BalanceHeaders => {
  const valueHeaderKeys = getFinancialsValueHeaderKeys({
    preferences,
    orderedAvailableColumns: ["total", "change", "changePercentage", "budgetForecast", "budget", "budgetDiff"],
  });

  const periodRange = getDateConfigRange(preferences);

  const periodType = preferences.periodType;
  const currentPeriod = getCurrentPeriod(periodType);
  const periods = getDateRangeEntries(periodRange, periodType);

  const periodHeaderConfigs: PeriodColumnHeaderConfigSet = { type: "period", headers: [] };
  const valueHeaderConfigs: ValueColumnHeaderConfig[][] = [];

  const headerItems: Array<{ period?: Moment; isGrandTotal?: boolean }> = periods.map((period) => ({ period }));
  if (preferences.showGrandTotal) {
    headerItems.push({ isGrandTotal: true });
  }

  headerItems.forEach((item, index) => {
    if (item.isGrandTotal) {
      const { groupHeader, valueHeaders } = generateGrandTotalHeaders({
        index,
        valueHeaderKeys,
        selectedBudgets,
      });

      valueHeaderConfigs.push(valueHeaders);
      periodHeaderConfigs.headers.push(groupHeader);
    } else if (item.period) {
      const period = item.period;
      const periodSuffix = formatPeriodKey(period);

      const groupHeader: GroupColumnHeaderConfig = {
        type: "period",
        key: periodSuffix + "period",
        label: { value: getPeriodHeader(period, periodType) },
        colSpan: 0,
        period,
        even: isEvenIndex(index),
      };

      const headers = getFinancialsValueHeaders({
        index,
        groupHeader,
        valueHeaderKeys,
        keyPrefix: periodSuffix,
      });

      const periodBudgets = getPeriodBudgetsIntersect(selectedBudgets, period, periodType);
      const forecast = period.isSameOrAfter(currentPeriod);

      headers.push(...getFinancialsBudgetValueHeaders({ index, forecast, groupHeader, prefix: periodSuffix, budgets: periodBudgets, valueHeaderKeys }));

      valueHeaderConfigs.push(headers);

      periodHeaderConfigs.headers.push(groupHeader);
    }
  });

  if (preferences.useChangeColors) {
    const configs = valueHeaderConfigs.flat().filter((h) => h.type === "changePercentage");

    configs.forEach((config) => {
      config.color = (row, value) => getChangePercentageCopyColor(value);
    });
  }

  return { groupHeaders: periodHeaderConfigs, valueHeaders: valueHeaderConfigs };
};

export const getFinancialsPropertyHeaders = ({
  properties,
  preferences,
}: {
  properties: Property[];
  preferences?: { showGrandTotal?: boolean };
}): BalanceHeaders => {
  const valueHeaderKeys = getFinancialsValueHeaderKeys({
    preferences: { columns: ["total"] },
    orderedAvailableColumns: ["total"],
  });

  const propertyHeaderConfigs: PropertyColumnHeaderConfigSet = { type: "property", headers: [] };
  const valueHeaderConfigs: ValueColumnHeaderConfig[][] = [];

  const headerItems: Array<{ property?: Property; isGrandTotal?: boolean }> = properties.map((property) => ({ property }));
  if (preferences?.showGrandTotal) {
    headerItems.push({ isGrandTotal: true });
  }

  headerItems.forEach((item, index) => {
    if (item.isGrandTotal) {
      const { groupHeader, valueHeaders } = generateGrandTotalHeaders({
        index,
        valueHeaderKeys,
        selectedBudgets: [],
      });

      valueHeaderConfigs.push(valueHeaders);
      propertyHeaderConfigs.headers.push(groupHeader);
    } else if (item.property) {
      const property = item.property;
      const even = isEvenIndex(index);
      const keyPrefix = formatPropertyKey(property);

      const groupHeader: GroupColumnHeaderConfig = {
        type: "property",
        key: keyPrefix + "period",
        label: { value: property.name ?? "" },
        colSpan: 0,
        property,
        even,
      };

      const headers = getFinancialsValueHeaders({
        index,
        groupHeader,
        keyPrefix,
        valueHeaderKeys,
      });

      valueHeaderConfigs.push(headers);

      groupHeader.colSpan = calculateColumnSpan(headers);

      propertyHeaderConfigs.headers.push(groupHeader);
    }
  });

  return { groupHeaders: propertyHeaderConfigs, valueHeaders: valueHeaderConfigs };
};

export const getHeadersDisplay = (headers: BalanceHeaders) => ({
  groupHeaders: headers.groupHeaders.headers.slice().reverse(),
  valueHeaders: headers.valueHeaders.slice().reverse().flat(),
});
